import { DocumentHit, SearchCategoryUrl, SearchHit } from '@common/types/search';
import { SearchResultsSection } from '@components/display/search';
import { isSearchHit } from '@constants/search';
import { useAppSelector } from '@hooks/generic';
import { SvgIconProps } from '@mui/material';
import { getTranslationFromRegionStrings } from '@utils/translations';
import { useMemo } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch';

export const CategoryResults = <T extends SearchHit | DocumentHit = SearchHit | DocumentHit>({
    title,
    chip,
    name,
    Icon,
    search,
    onShowNext,
    urlIn,
    url,
    RenderComponent = SearchResultsSection,
    hitsPerPage = 3,
    downloadable = false,
}: {
    name: string;
    Icon: React.FC<SvgIconProps>;
    search: string;
    onShowNext: (newHitsPerPage: number) => void;
    urlIn?: boolean;
    url: SearchCategoryUrl<T>;
    RenderComponent?: React.ElementType;
    hitsPerPage?: number;
    downloadable?: boolean;
    chip?: (item: T) => React.ReactNode;
    title?: (item: T) => string;
}) => {
    const { results } = useHits<T>();
    const { status } = useInstantSearch();
    const locale = useAppSelector((state) => state.translation.locale);
    const isLoading = status === 'loading';

    const parsedResults = useMemo(
        () =>
            isLoading
                ? Array(hitsPerPage).fill({ title: '' })
                : results?.hits.map((hit) => ({
                      ...hit,
                      title:
                          title?.(hit) ??
                          (isSearchHit(hit)
                              ? hit.title ?? hit.name ?? ''
                              : (getTranslationFromRegionStrings(hit.document_name ?? {}, locale) ||
                                    hit.name_translation) ??
                                ''),
                      type: isSearchHit(hit) ? hit.suffix ?? '' : '',
                      url: {
                          href: url.getHref(hit),
                          target: url.getTarget?.(hit) ?? url.target,
                      },
                  })) ?? [],
        [results, isLoading, locale, title, url, hitsPerPage]
    );

    return (
        <RenderComponent
            Icon={Icon}
            name={name}
            results={parsedResults}
            search={search}
            totalResultsCount={results?.nbHits}
            onShowNext={onShowNext}
            loading={isLoading}
            urlIn={urlIn}
            hitsPerPage={hitsPerPage}
            downloadable={downloadable}
            chip={chip}
            title={title}
        />
    );
};
